import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileText, DollarSign, Clock, CheckCircle, AlertTriangle, Plus,
  Loader2, Send, Download, Eye, Trash2, Edit, MapPin, ChevronRight,
  Calendar, Building, User, Phone, Mail, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showExternalDialog, setShowExternalDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deliveredLoads, setDeliveredLoads] = useState([]);
  const [selectedLoadId, setSelectedLoadId] = useState('');
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const printRef = useRef();

  // External invoice form state
  const [externalForm, setExternalForm] = useState({
    bill_to_name: '',
    bill_to_company: '',
    bill_to_email: '',
    bill_to_phone: '',
    bill_to_address: '',
    pickup_location: '',
    delivery_location: '',
    pickup_date: '',
    delivery_date: '',
    vehicle_info: '',
    load_category: 'auto_transport',
    amount: '',
    notes: '',
    payment_terms: 'Net 30'
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    bill_to_name: '',
    bill_to_company: '',
    bill_to_email: '',
    bill_to_phone: '',
    bill_to_address: '',
    amount: '',
    notes: '',
    payment_terms: 'Due on receipt'
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/invoices/stats')
      ]);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveredLoads = async () => {
    try {
      // Get delivered loads that don't have invoices yet
      const loadsRes = await api.get('/my-loads?status=delivered');
      const loads = loadsRes.data || [];
      
      // Check which ones already have invoices
      const loadsWithoutInvoices = [];
      for (const load of loads) {
        try {
          const checkRes = await api.get(`/invoices/load/${load.id}`);
          if (!checkRes.data.has_invoice) {
            loadsWithoutInvoices.push(load);
          }
        } catch {
          loadsWithoutInvoices.push(load);
        }
      }
      setDeliveredLoads(loadsWithoutInvoices);
    } catch (error) {
      console.error('Failed to fetch delivered loads:', error);
    }
  };

  const handleOpenCreateDialog = async () => {
    setSelectedLoadId('');
    await fetchDeliveredLoads();
    setShowCreateDialog(true);
  };

  const handleCreateInvoice = async () => {
    if (!selectedLoadId) {
      toast.error('Please select a load');
      return;
    }

    setCreating(true);
    try {
      await api.post('/invoices', { load_id: selectedLoadId });
      toast.success('Invoice created successfully');
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateExternalInvoice = async () => {
    if (!externalForm.amount || parseFloat(externalForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!externalForm.bill_to_company && !externalForm.bill_to_name) {
      toast.error('Please enter customer name or company');
      return;
    }

    setCreating(true);
    try {
      await api.post('/invoices', {
        is_external: true,
        bill_to_name: externalForm.bill_to_name || null,
        bill_to_company: externalForm.bill_to_company || null,
        bill_to_email: externalForm.bill_to_email || null,
        bill_to_phone: externalForm.bill_to_phone || null,
        bill_to_address: externalForm.bill_to_address || null,
        pickup_location: externalForm.pickup_location || null,
        delivery_location: externalForm.delivery_location || null,
        pickup_date: externalForm.pickup_date ? new Date(externalForm.pickup_date).toISOString() : null,
        delivery_date: externalForm.delivery_date ? new Date(externalForm.delivery_date).toISOString() : null,
        vehicle_info: externalForm.vehicle_info || null,
        load_category: externalForm.load_category || null,
        amount: parseFloat(externalForm.amount),
        notes: externalForm.notes || null,
        payment_terms: externalForm.payment_terms || 'Net 30'
      });
      toast.success('Invoice created successfully');
      setShowExternalDialog(false);
      setExternalForm({
        bill_to_name: '',
        bill_to_company: '',
        bill_to_email: '',
        bill_to_phone: '',
        bill_to_address: '',
        pickup_location: '',
        delivery_location: '',
        pickup_date: '',
        delivery_date: '',
        vehicle_info: '',
        load_category: 'auto_transport',
        amount: '',
        notes: '',
        payment_terms: 'Net 30'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const res = await api.get(`/invoices/${invoice.id}`);
      setSelectedInvoice(res.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleOpenEditDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setEditForm({
      bill_to_name: invoice.bill_to_name || '',
      bill_to_company: invoice.bill_to_company || '',
      bill_to_email: invoice.bill_to_email || '',
      bill_to_phone: invoice.bill_to_phone || '',
      bill_to_address: invoice.bill_to_address || '',
      amount: invoice.amount?.toString() || '',
      notes: invoice.notes || '',
      payment_terms: invoice.payment_terms || 'Due on receipt'
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...editForm,
        amount: parseFloat(editForm.amount) || undefined
      };
      await api.put(`/invoices/${selectedInvoice.id}`, updateData);
      toast.success('Invoice updated');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkSent = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/send`);
      toast.success('Invoice marked as sent');
      fetchData();
      if (showViewDialog) {
        const res = await api.get(`/invoices/${invoiceId}`);
        setSelectedInvoice(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as sent');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/paid`);
      toast.success('Invoice marked as paid');
      fetchData();
      if (showViewDialog) {
        const res = await api.get(`/invoices/${invoiceId}`);
        setSelectedInvoice(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      setShowViewDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${selectedInvoice?.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
            .invoice-title { font-size: 32px; font-weight: bold; color: #4ADE80; }
            .invoice-number { font-size: 14px; color: #666; margin-top: 5px; }
            .addresses { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .address-block { width: 45%; }
            .address-label { font-size: 12px; color: #999; text-transform: uppercase; margin-bottom: 8px; }
            .address-content { font-size: 14px; line-height: 1.6; }
            .load-details { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .load-title { font-weight: bold; margin-bottom: 15px; }
            .load-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            .totals { margin-top: 30px; text-align: right; }
            .total-row { padding: 8px 0; }
            .grand-total { font-size: 24px; font-weight: bold; color: #4ADE80; margin-top: 15px; }
            .footer { margin-top: 50px; text-align: center; color: #999; font-size: 12px; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getFilteredInvoices = () => {
    if (filter === 'all') return invoices;
    return invoices.filter(inv => inv.status === filter);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-600">Draft</span>;
      case 'sent':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">Sent</span>;
      case 'paid':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">Paid</span>;
      case 'overdue':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">Overdue</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-600">{status}</span>;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const filteredInvoices = getFilteredInvoices();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 pt-20 flex items-center justify-center">
        <Card className="max-w-md border-gray-200 bg-white">
          <CardContent className="pt-6 text-center">
            <FileText className="w-12 h-12 mx-auto text-[#3B82F6] mb-4" />
            <h2 className="text-xl font-bold mb-2">Sign In Required</h2>
            <p className="text-gray-600 mb-4">Please sign in to access invoices.</p>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-20 pb-12 px-3 sm:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0D1B2A] flex items-center gap-2 sm:gap-3">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              Invoices
            </h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              Create and manage invoices for completed loads
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              onClick={handleOpenCreateDialog} 
              variant="outline"
              className="flex-1 sm:flex-none border-[#3B82F6]/30"
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> From Load
            </Button>
            <Button 
              onClick={() => setShowExternalDialog(true)} 
              className="bg-[#4ADE80] hover:bg-[#22C55E] text-black flex-1 sm:flex-none"
              data-testid="create-external-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Invoice
            </Button>
          </div>
        </div>

        {/* Stats Cards - Horizontal scroll on mobile */}
        {stats && (
          <div className="overflow-x-auto pb-2 mb-6 sm:mb-8 -mx-3 px-3 sm:mx-0 sm:px-0 mobile-scrollbar">
            <div className="flex sm:grid sm:grid-cols-5 gap-3 sm:gap-4 min-w-max sm:min-w-0 pr-6 sm:pr-0">
              <Card className="border-gray-200 bg-white min-w-[100px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Total</p>
                      <p className="text-lg sm:text-xl font-bold text-[#0D1B2A]">{stats.total_invoices}</p>
                    </div>
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-[#3B82F6] flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white min-w-[100px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Draft</p>
                      <p className="text-lg sm:text-xl font-bold text-gray-600">{stats.draft_count}</p>
                    </div>
                    <Edit className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white min-w-[100px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Sent</p>
                      <p className="text-lg sm:text-xl font-bold text-blue-400">{stats.sent_count}</p>
                    </div>
                    <Send className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white min-w-[100px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Paid</p>
                      <p className="text-lg sm:text-xl font-bold text-green-400">{stats.paid_count}</p>
                    </div>
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white min-w-[120px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase whitespace-nowrap">Outstanding</p>
                      <p className="text-lg sm:text-xl font-bold text-yellow-400">{formatCurrency(stats.outstanding_amount)}</p>
                    </div>
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Filter Tabs - Horizontal scroll on mobile */}
        <div className="overflow-x-auto pb-2 mb-4 sm:mb-6 -mx-3 px-3 sm:mx-0 sm:px-0 mobile-scrollbar">
          <div className="flex items-center gap-2 min-w-max sm:min-w-0 pr-6 sm:pr-0">
            {[
              { key: 'all', label: 'All', count: invoices.length },
              { key: 'draft', label: 'Draft', count: invoices.filter(i => i.status === 'draft').length },
              { key: 'sent', label: 'Sent', count: invoices.filter(i => i.status === 'sent').length },
              { key: 'paid', label: 'Paid', count: invoices.filter(i => i.status === 'paid').length },
              { key: 'overdue', label: 'Overdue', count: invoices.filter(i => i.status === 'overdue').length },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                data-testid={`filter-${tab.key}`}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  filter === tab.key
                    ? 'bg-[#3B82F6] text-[#0D1B2A]'
                    : 'bg-white text-gray-600 hover:text-[#0D1B2A] hover:bg-[#1E3A5F]'
                }`}
              >
                {tab.label} <span className="ml-1 text-xs opacity-70">({tab.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="border-gray-200 bg-white">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-[#0D1B2A] mb-2">No Invoices Found</h3>
              <p className="text-gray-600 mb-6">
                {filter === 'all' 
                  ? 'Create your first invoice from a completed load.' 
                  : `No ${filter} invoices at the moment.`}
              </p>
              {filter === 'all' && (
                <Button onClick={handleOpenCreateDialog} className="bg-[#4ADE80] hover:bg-[#22C55E] text-black">
                  <Plus className="w-4 h-4 mr-2" /> Create Invoice
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredInvoices.map((invoice) => (
              <Card 
                key={invoice.id} 
                className="border-gray-200 bg-white hover:border-[#3B82F6]/40 transition-all cursor-pointer"
                onClick={() => handleViewInvoice(invoice)}
                data-testid={`invoice-card-${invoice.id}`}
              >
                <CardContent className="p-3 sm:p-4">
                  {/* Mobile Layout */}
                  <div className="sm:hidden">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-[#0D1B2A] font-mono font-medium text-sm">{invoice.invoice_number}</span>
                        <div className="mt-1">{getStatusBadge(invoice.status)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">{formatCurrency(invoice.total_amount)}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-300 mb-2">
                      <div className="flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 text-green-400 flex-shrink-0" />
                        <span className="truncate">{invoice.pickup_location || 'N/A'}</span>
                        <ChevronRight className="w-3 h-3 text-gray-500 flex-shrink-0" />
                        <MapPin className="w-3 h-3 text-red-400 flex-shrink-0" />
                        <span className="truncate">{invoice.delivery_location || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      {invoice.bill_to_company || invoice.bill_to_name || 'No customer'}
                      {invoice.due_date && ` • Due: ${formatDate(invoice.due_date)}`}
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {invoice.status === 'draft' && (
                        <>
                          <Button size="sm" onClick={() => handleMarkSent(invoice.id)} className="bg-blue-500 hover:bg-blue-600 flex-1">
                            <Send className="w-4 h-4 mr-1" /> Send
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleOpenEditDialog(invoice)} className="border-[#3B82F6]/30">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                        <Button size="sm" onClick={() => handleMarkPaid(invoice.id)} className="bg-green-500 hover:bg-green-600 text-black flex-1">
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleViewInvoice(invoice)} className="text-gray-600">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:flex items-center justify-between">
                    {/* Invoice Info */}
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col gap-1">
                        <span className="text-[#0D1B2A] font-mono font-medium">{invoice.invoice_number}</span>
                        {getStatusBadge(invoice.status)}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 text-[#0D1B2A] font-medium">
                          <MapPin className="w-4 h-4 text-green-400" />
                          {invoice.pickup_location || 'N/A'}
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                          <MapPin className="w-4 h-4 text-red-400" />
                          {invoice.delivery_location || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {invoice.bill_to_company || invoice.bill_to_name || 'No customer info'}
                          {invoice.due_date && ` • Due: ${formatDate(invoice.due_date)}`}
                        </div>
                      </div>
                    </div>

                    {/* Amount & Actions */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xl font-bold text-green-600">{formatCurrency(invoice.total_amount)}</div>
                        <div className="text-xs text-gray-500">Issued: {formatDate(invoice.issue_date)}</div>
                      </div>

                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {invoice.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleMarkSent(invoice.id)}
                              className="bg-blue-500 hover:bg-blue-600"
                              data-testid={`mark-sent-${invoice.id}`}
                            >
                              <Send className="w-4 h-4 mr-1" /> Send
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEditDialog(invoice)}
                              className="border-[#3B82F6]/30"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkPaid(invoice.id)}
                            className="bg-green-500 hover:bg-green-600 text-black"
                            data-testid={`mark-paid-${invoice.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewInvoice(invoice)}
                          className="text-gray-600 hover:text-[#0D1B2A]"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Invoice Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-white border-gray-200 max-w-md">
            <DialogHeader>
              <DialogTitle>Create Invoice</DialogTitle>
              <DialogDescription>
                Select a delivered load to create an invoice
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {deliveredLoads.length === 0 ? (
                <div className="p-4 text-center bg-white rounded-lg">
                  <AlertTriangle className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
                  <p className="text-sm text-gray-600">No delivered loads without invoices</p>
                  <p className="text-xs text-gray-500 mt-1">Complete a delivery first to create an invoice</p>
                </div>
              ) : (
                <Select value={selectedLoadId} onValueChange={setSelectedLoadId}>
                  <SelectTrigger className="bg-[#0A1628] border-gray-200">
                    <SelectValue placeholder="Select a delivered load" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    {deliveredLoads.map(load => (
                      <SelectItem key={load.id} value={load.id}>
                        <div className="flex flex-col">
                          <span>{load.pickup_location} → {load.delivery_location}</span>
                          <span className="text-xs text-gray-500">${load.price?.toLocaleString()} • {formatDate(load.pickup_date)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateInvoice}
                  disabled={creating || !selectedLoadId}
                  className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                  data-testid="confirm-create-invoice"
                >
                  {creating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                  ) : (
                    'Create Invoice'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-white border-gray-200 max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Invoice</DialogTitle>
              <DialogDescription>
                Update invoice details
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={editForm.bill_to_name}
                    onChange={(e) => setEditForm({...editForm, bill_to_name: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-gray-200"
                  />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input
                    value={editForm.bill_to_company}
                    onChange={(e) => setEditForm({...editForm, bill_to_company: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-gray-200"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.bill_to_email}
                    onChange={(e) => setEditForm({...editForm, bill_to_email: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-gray-200"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editForm.bill_to_phone}
                    onChange={(e) => setEditForm({...editForm, bill_to_phone: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-gray-200"
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  value={editForm.bill_to_address}
                  onChange={(e) => setEditForm({...editForm, bill_to_address: e.target.value})}
                  className="mt-1 bg-[#0A1628] border-gray-200"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                    className="mt-1 bg-[#0A1628] border-gray-200"
                  />
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Select 
                    value={editForm.payment_terms} 
                    onValueChange={(v) => setEditForm({...editForm, payment_terms: v})}
                  >
                    <SelectTrigger className="mt-1 bg-[#0A1628] border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="Due on receipt">Due on Receipt</SelectItem>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="mt-1 bg-[#0A1628] border-gray-200"
                  placeholder="Additional notes or payment instructions..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Invoice Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Invoice {selectedInvoice?.invoice_number}</span>
                {selectedInvoice && getStatusBadge(selectedInvoice.status)}
              </DialogTitle>
            </DialogHeader>

            {selectedInvoice && (
              <div>
                {/* Printable Invoice Content */}
                <div ref={printRef} className="bg-white text-black p-6 rounded-lg">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-8">
                    <div>
                      <h1 className="text-3xl font-bold text-green-600">INVOICE</h1>
                      <p className="text-gray-600 mt-1">{selectedInvoice.invoice_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Issue Date: {formatDate(selectedInvoice.issue_date)}</p>
                      {selectedInvoice.due_date && (
                        <p className="text-sm text-gray-600">Due Date: {formatDate(selectedInvoice.due_date)}</p>
                      )}
                    </div>
                  </div>

                  {/* Addresses */}
                  <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">Bill From</p>
                      <p className="font-semibold">{selectedInvoice.bill_from_company || selectedInvoice.bill_from_name || 'Your Company'}</p>
                      {selectedInvoice.bill_from_name && selectedInvoice.bill_from_company && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_from_name}</p>
                      )}
                      {selectedInvoice.bill_from_email && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_from_email}</p>
                      )}
                      {selectedInvoice.bill_from_phone && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_from_phone}</p>
                      )}
                      {selectedInvoice.bill_from_mc && (
                        <p className="text-sm text-gray-600">MC# {selectedInvoice.bill_from_mc}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">Bill To</p>
                      <p className="font-semibold">{selectedInvoice.bill_to_company || selectedInvoice.bill_to_name || 'Customer'}</p>
                      {selectedInvoice.bill_to_name && selectedInvoice.bill_to_company && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_to_name}</p>
                      )}
                      {selectedInvoice.bill_to_email && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_to_email}</p>
                      )}
                      {selectedInvoice.bill_to_phone && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_to_phone}</p>
                      )}
                      {selectedInvoice.bill_to_address && (
                        <p className="text-sm text-gray-600">{selectedInvoice.bill_to_address}</p>
                      )}
                    </div>
                  </div>

                  {/* Load Details */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h3 className="font-semibold text-sm uppercase text-gray-500 mb-3">Shipment Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Route</span>
                        <span className="font-medium">
                          {selectedInvoice.pickup_location} → {selectedInvoice.delivery_location}
                        </span>
                      </div>
                      {selectedInvoice.pickup_date && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pickup Date</span>
                          <span>{formatDate(selectedInvoice.pickup_date)}</span>
                        </div>
                      )}
                      {selectedInvoice.delivery_date && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Delivery Date</span>
                          <span>{formatDate(selectedInvoice.delivery_date)}</span>
                        </div>
                      )}
                      {selectedInvoice.vehicle_info && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Vehicle(s)</span>
                          <span>{selectedInvoice.vehicle_info}</span>
                        </div>
                      )}
                      {selectedInvoice.load_category && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Category</span>
                          <span className="capitalize">{selectedInvoice.load_category.replace('_', ' ')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Subtotal</span>
                      <span>{formatCurrency(selectedInvoice.amount)}</span>
                    </div>
                    {selectedInvoice.tax_amount > 0 && (
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Tax ({selectedInvoice.tax_rate}%)</span>
                        <span>{formatCurrency(selectedInvoice.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 border-t mt-2">
                      <span className="text-xl font-bold">Total Due</span>
                      <span className="text-xl font-bold text-green-600">{formatCurrency(selectedInvoice.total_amount)}</span>
                    </div>
                  </div>

                  {/* Payment Terms & Notes */}
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Payment Terms:</span> {selectedInvoice.payment_terms}
                    </p>
                    {selectedInvoice.notes && (
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">Notes:</span> {selectedInvoice.notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  {selectedInvoice.status === 'draft' && (
                    <>
                      <Button
                        onClick={() => handleMarkSent(selectedInvoice.id)}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        <Send className="w-4 h-4 mr-2" /> Mark as Sent
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setShowViewDialog(false); handleOpenEditDialog(selectedInvoice); }}
                        className="border-[#3B82F6]/30"
                      >
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </>
                  )}
                  {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                    <Button
                      onClick={() => handleMarkPaid(selectedInvoice.id)}
                      className="bg-green-500 hover:bg-green-600 text-black"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Mark as Paid
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={handlePrint}
                    className="border-[#3B82F6]/30"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print / PDF
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowViewDialog(false)}
                    className="ml-auto"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* External Invoice Dialog */}
        <Dialog open={showExternalDialog} onOpenChange={setShowExternalDialog}>
          <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Create Invoice (External Load)
              </DialogTitle>
              <DialogDescription>
                Create an invoice for a load not in the system
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-green-600 flex items-center gap-2">
                  <Building className="w-4 h-4" /> Bill To (Customer)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Company Name</Label>
                    <Input
                      value={externalForm.bill_to_company}
                      onChange={(e) => setExternalForm({...externalForm, bill_to_company: e.target.value})}
                      placeholder="ABC Logistics"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Contact Name</Label>
                    <Input
                      value={externalForm.bill_to_name}
                      onChange={(e) => setExternalForm({...externalForm, bill_to_name: e.target.value})}
                      placeholder="John Doe"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Email</Label>
                    <Input
                      type="email"
                      value={externalForm.bill_to_email}
                      onChange={(e) => setExternalForm({...externalForm, bill_to_email: e.target.value})}
                      placeholder="billing@company.com"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Phone</Label>
                    <Input
                      value={externalForm.bill_to_phone}
                      onChange={(e) => setExternalForm({...externalForm, bill_to_phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Billing Address</Label>
                  <Input
                    value={externalForm.bill_to_address}
                    onChange={(e) => setExternalForm({...externalForm, bill_to_address: e.target.value})}
                    placeholder="123 Main St, City, State 12345"
                    className="mt-1 bg-[#0A1628] border-gray-200"
                  />
                </div>
              </div>

              {/* Load Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-[#3B82F6] flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Load Details
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Pickup Location</Label>
                    <Input
                      value={externalForm.pickup_location}
                      onChange={(e) => setExternalForm({...externalForm, pickup_location: e.target.value})}
                      placeholder="Los Angeles, CA"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Delivery Location</Label>
                    <Input
                      value={externalForm.delivery_location}
                      onChange={(e) => setExternalForm({...externalForm, delivery_location: e.target.value})}
                      placeholder="Dallas, TX"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Pickup Date</Label>
                    <Input
                      type="date"
                      value={externalForm.pickup_date}
                      onChange={(e) => setExternalForm({...externalForm, pickup_date: e.target.value})}
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Delivery Date</Label>
                    <Input
                      type="date"
                      value={externalForm.delivery_date}
                      onChange={(e) => setExternalForm({...externalForm, delivery_date: e.target.value})}
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Vehicle/Cargo Info</Label>
                    <Input
                      value={externalForm.vehicle_info}
                      onChange={(e) => setExternalForm({...externalForm, vehicle_info: e.target.value})}
                      placeholder="2023 Toyota Camry"
                      className="mt-1 bg-[#0A1628] border-gray-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Category</Label>
                    <Select 
                      value={externalForm.load_category} 
                      onValueChange={(v) => setExternalForm({...externalForm, load_category: v})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A1628] border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="auto_transport">Auto Transport</SelectItem>
                        <SelectItem value="freight">Freight</SelectItem>
                        <SelectItem value="ltl">LTL</SelectItem>
                        <SelectItem value="flatbed">Flatbed</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Invoice Details
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Amount ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={externalForm.amount}
                      onChange={(e) => setExternalForm({...externalForm, amount: e.target.value})}
                      placeholder="1500.00"
                      className="mt-1 bg-[#0A1628] border-gray-200 text-lg"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Payment Terms</Label>
                    <Select 
                      value={externalForm.payment_terms} 
                      onValueChange={(v) => setExternalForm({...externalForm, payment_terms: v})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A1628] border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="Due on receipt">Due on Receipt</SelectItem>
                        <SelectItem value="Net 15">Net 15</SelectItem>
                        <SelectItem value="Net 30">Net 30</SelectItem>
                        <SelectItem value="Net 45">Net 45</SelectItem>
                        <SelectItem value="Net 60">Net 60</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Notes / Description</Label>
                  <Textarea
                    value={externalForm.notes}
                    onChange={(e) => setExternalForm({...externalForm, notes: e.target.value})}
                    placeholder="Additional details about the service..."
                    className="mt-1 bg-[#0A1628] border-gray-200"
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowExternalDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateExternalInvoice}
                  disabled={creating}
                  className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Invoice
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default InvoicesPage;
